import { FlowchartNodeDto, FlowchartWidget, GuidelineWidget, LinkElementType, ComponentListWidget } from '@treetop/shared/model';

import { HtmlUtil } from '../../../../shared/feature-flowchart/src/lib/util/html.util';

/**
 * Prepares a widget for copying by stripping transient, context-specific properties
 * that should not carry over to the pasted copy:
 * - `tag` and `parentId` are cleared so the paste target can assign its own values.
 * - `links` are filtered to keep only external links; internal links (to chapters or
 *   other guideline elements) would become stale in a different location.
 * - For flowchart widgets, the same link-stripping is applied recursively to every
 *   element, and `guidelineChapterId` references are cleared.
 * - For chapter widgets, `widgetIds` and `childrenIds` are cleared — only the chapter
 *   shell itself is copied, not its children.
 */
export function stripWidget(widget: GuidelineWidget): GuidelineWidget {
  const stripped = {
    ...widget,
    tag: undefined,
    parentId: undefined,
    links: (widget.links ?? []).filter((link) => link.toElementType === LinkElementType.External),
  };

  if (stripped.widgetType === 'flowchart') {
    return {
      ...stripped,
      elements: stripFlowchartElements(stripped.elements ?? []),
    };
  }

  if (stripped.widgetType === 'chapter') {
    return {
      ...stripped,
      widgetIds: [],
      childrenIds: [],
    };
  }

  if (stripped.widgetType === 'text') {
    const newText = HtmlUtil.removeLinksFromHtml(stripped.text ?? '');
    const sanitizedText = newText.replace(/<mark[^>]*>(.*?)<\/mark>/g, '$1');

    return {
      ...stripped,
      text: sanitizedText === '' ? undefined : sanitizedText,
    };
  }

  return stripped;
}

/**
 * Produces a deep copy of a flowchart widget with all internal IDs replaced by fresh
 * UUIDs, so the pasted copy is fully independent from the original.
 *
 * The remapping is done in two passes:
 * 1. Collect every node `id` from the entire element tree and map it to a new UUID.
 * 2. Walk the tree again, replacing `id`, `uniqueId`, `parentId`, and all
 *    `outgoingLinks` source/target IDs using the map built in pass 1.
 *
 * This ensures that links between nodes within the same flowchart keep pointing to the
 * correct (new) nodes after the copy.
 */
export function remapFlowchartElementIds(widget: FlowchartWidget): FlowchartWidget {
  if (!widget.elements?.length) return widget;

  const idMap = new Map<string, string>();

  const collectIds = (nodes: FlowchartNodeDto[]) => {
    for (const node of nodes) {
      if (node.id) idMap.set(node.id, crypto.randomUUID());
      if (node.children?.length) collectIds(node.children);
    }
  };

  collectIds(widget.elements);

  const remapNode = (node: FlowchartNodeDto): FlowchartNodeDto => ({
    ...node,
    id: node.id ? (idMap.get(node.id) ?? node.id) : node.id,
    uniqueId: crypto.randomUUID(),
    parentId: node.parentId ? (idMap.get(node.parentId) ?? node.parentId) : node.parentId,
    children: node.children?.map(remapNode),
    outgoingLinks: node.outgoingLinks?.map((link) => ({
      ...link,
      id: crypto.randomUUID(),
      fromFlowchartNodeId: link.fromFlowchartNodeId
        ? (idMap.get(link.fromFlowchartNodeId) ?? link.fromFlowchartNodeId)
        : link.fromFlowchartNodeId,
      toFlowchartNodeId: link.toFlowchartNodeId ? (idMap.get(link.toFlowchartNodeId) ?? link.toFlowchartNodeId) : link.toFlowchartNodeId,
    })),
  });

  return {
    ...widget,
    elements: widget.elements.map(remapNode),
  };
}

/**
 * Recursively strips flowchart element nodes of context-specific data:
 * - Clears `guidelineChapterId` so the elements are not tied to the source chapter.
 * - Filters out non-external links for the same reason as in `stripWidget`.
 *
 * Applied to the full element tree depth-first via the `children` array.
 */
function stripFlowchartElements(nodes: FlowchartNodeDto[]): FlowchartNodeDto[] {
  return nodes.map((node) => ({
    ...node,
    guidelineChapterId: null,
    links: (node.links ?? []).filter((link) => link.toElementType === LinkElementType.External),
    children: node.children ? stripFlowchartElements(node.children) : node.children,
  }));
}

type WidgetWithIds = GuidelineWidget & { widgetIds?: string[]; childrenIds?: string[] };

/**
 * Collects all widgets that are transitively referenced by the given widget through
 * its `widgetIds` and `childrenIds` arrays, looked up from `widgetMap`.
 *
 * The traversal is breadth-agnostic and works for any widget type that carries
 * `widgetIds` or `childrenIds` — e.g. component-list → component → text,
 * or (in principle) chapter → any child widget.
 *
 * A `visited` set prevents infinite loops in case of circular references.
 * The root widget itself is NOT included in the result; only its descendants are.
 */
export function collectRelatedWidgets(widget: GuidelineWidget, widgetMap: Record<string, GuidelineWidget>): GuidelineWidget[] {
  const related: GuidelineWidget[] = [];
  const visited = new Set<string>();

  const collect = (id: string): void => {
    if (visited.has(id)) return;
    visited.add(id);

    const w = widgetMap[id] as WidgetWithIds | undefined;
    if (!w) return;

    related.push(w);

    for (const childId of w.widgetIds ?? []) {
      collect(childId);
    }
    for (const childId of w.childrenIds ?? []) {
      collect(childId);
    }
  };

  const root = widget as WidgetWithIds;
  for (const id of root.widgetIds ?? []) {
    collect(id);
  }
  for (const id of root.childrenIds ?? []) {
    collect(id);
  }

  return related;
}

/**
 * Assigns fresh UUIDs to a component-list widget and all of its related (child) widgets,
 * rewriting every cross-reference so the resulting set is self-consistent and fully
 * independent from the originals.
 *
 * Steps:
 * 1. Build an `idMap` covering the root widget and every related widget.
 * 2. Remap the root widget's `uniqueId` and `widgetIds`.
 * 3. Remap each related widget's `uniqueId`, `parentId`, `widgetIds`, and `childrenIds`
 *    using the same map, so all parent↔child links remain valid after the copy.
 *
 * Returns the remapped root widget and the remapped related widgets as a tuple so the
 * caller can insert them all into the guideline graph together.
 */
export function remapComponentListIds(
  widget: ComponentListWidget,
  relatedWidgets: GuidelineWidget[],
): { widget: ComponentListWidget; relatedWidgets: GuidelineWidget[] } {
  const idMap = new Map<string, string>();
  const widgetId = widget.uniqueId ?? crypto.randomUUID();
  idMap.set(widgetId, crypto.randomUUID());
  for (const w of relatedWidgets) {
    if (w.uniqueId) idMap.set(w.uniqueId, crypto.randomUUID());
  }

  const remap = (id: string): string => idMap.get(id) ?? id;

  const remappedWidget: ComponentListWidget = {
    ...widget,
    uniqueId: remap(widgetId),
    widgetIds: widget.widgetIds?.map(remap),
  };

  const remappedRelated: GuidelineWidget[] = relatedWidgets.map((w) => {
    const id = w.uniqueId ?? crypto.randomUUID();
    const base = { ...w, uniqueId: remap(id), parentId: w.parentId ? remap(w.parentId) : undefined } as WidgetWithIds;

    return {
      ...base,
      widgetIds: base.widgetIds?.map(remap),
      childrenIds: base.childrenIds?.map(remap),
    } as unknown as GuidelineWidget;
  });

  return { widget: remappedWidget, relatedWidgets: remappedRelated };
}
